import { FieldValues, UseFormGetValues, UseFormWatch } from 'react-hook-form'
import { DataClass } from 'scrivito'

import { DataClassMap, FieldMap } from './types'
import { getCustomFieldKey } from '../dataItem/getCustomFieldKey'
import { getDataClassEnumSelectArray } from '../enum/getDataClassEnumSelectArray'
import { getReferenceOptionsFunction } from '../reference'
import {
  AttributeType,
  SchemaAttributes,
  SchemaGeneral,
  SchemaGroups,
} from '@/Data/typesV2'
import { getSchemaName } from '@/utils/dataClassMap'
import { getSchema } from '@/utils/getSchema'
import {
  getLocalizedSchemaAttributes,
  getLocalizedSchemaGeneral,
  getLocalizedSchemaGroups,
} from '@/utils/schemaLocalization'

type AttributeOptions = {
  [attribute: string]: { default?: string; limitEnumValuesTo?: string[] }
}

/**
 * Builds the form field map for a single schema attribute.
 *
 * In contrast to {@link getDataClassFormMap} this does not depend on the
 * attribute being part of a schema group and does not skip readonly
 * attributes. Use it for forms that render a fixed, well-known set of fields.
 *
 * @param attribute - Name of the schema attribute.
 * @param schemaAttributes - Localized schema attributes of the data class.
 * @param getValues - Form accessor used to resolve enum value constraints.
 * @param watch - Form watcher used to rerender dependent enum fields.
 * @param attributeOptions - Optional per-attribute defaults and enum filters.
 * @returns The field map, or `undefined` if the attribute is not in the schema.
 */
export const getDataClassFieldMap = (
  attribute: string,
  schemaAttributes: SchemaAttributes | undefined,
  getValues: UseFormGetValues<FieldValues>,
  watch: UseFormWatch<FieldValues>,
  attributeOptions?: AttributeOptions,
): FieldMap | undefined => {
  const attributeSchema = schemaAttributes?.[attribute]
  if (!attributeSchema) return undefined

  const attributeKey = getCustomFieldKey(attribute, attributeSchema, watch)
  const { title, type, enumValues, multiLine } = attributeSchema

  return {
    fieldKey: attributeKey,
    name: attribute,
    fieldSchema: attributeSchema,
    title: title,
    value: attributeOptions?.[attribute]?.default,
    type: type as AttributeType,
    isMultiline: multiLine,
    enumValueOptions: getDataClassEnumSelectArray(
      getValues,
      enumValues,
      schemaAttributes,
      attributeOptions?.[attribute]?.limitEnumValuesTo,
    ),
    reference: {
      loadReferenceOptions: getReferenceOptionsFunction(attributeSchema),
    },
  }
}

export const getDataClassFormMap = (
  dataClass: DataClass,
  getValues: UseFormGetValues<FieldValues>,
  watch: UseFormWatch<FieldValues>,
  options?: {
    attributeOptions?: {
      [attribute: string]: { default?: string; limitEnumValuesTo?: string[] }
    }
  },
): DataClassMap => {
  const schemaName = getSchemaName(dataClass.name()!)
  const { schema } = getSchema(schemaName)

  const schemaGroups = getLocalizedSchemaGroups(
    schema?.get('groups') as SchemaGroups,
  ) as SchemaGroups

  const schemaGeneral = getLocalizedSchemaGeneral(
    schema?.get('general') as SchemaGeneral,
  )
  const groupsOrder = schemaGeneral?.groupOrder

  const schemaAttributes = getLocalizedSchemaAttributes(
    schema?.get('attributes') as SchemaAttributes,
  )

  if (!groupsOrder || !schemaAttributes) return []

  const dataItemMap: DataClassMap = groupsOrder.flatMap((groupName) => {
    const group = schemaGroups[groupName]
    if (!group) return []

    const fieldsMap: FieldMap[] = group.attributeOrder.flatMap((attribute) => {
      const attributeSchema = schemaAttributes[attribute]
      if (!attributeSchema) return []

      const isRequired = attributeSchema?.mandatory
      const isReadonly = attributeSchema?.readonly

      // readonly and not required => backend will create the value
      if (isReadonly && !isRequired) return []

      const fieldMap = getDataClassFieldMap(
        attribute,
        schemaAttributes,
        getValues,
        watch,
        options?.attributeOptions,
      )

      return fieldMap ?? []
    })

    return {
      name: groupName,
      title: group.title,
      fields: fieldsMap,
    }
  })

  return dataItemMap
}
